// Lib
import Placeholder from '@tiptap/extension-placeholder';
import type { PlaceholderOptions } from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import { defer } from 'lodash';
import { useMemo } from 'react';

// Custom extensions
import { Bold } from '../extensions/Bold';
import {
    DEFAULT_EDITOR_SYNC_GET_DEBOUNCE_TIME,
    DEFAULT_EDITOR_SYNC_GET_DEBOUNCE_COUNT,
    ExternalStoreSynchroniser,
} from '../extensions/externalStoreSync/ExternalStoreSynchroniser';
import { EmptyDelete } from '../extensions/EmptyDelete';
import { Italic } from '../extensions/Italic';
import { Paragraph } from '../extensions/Paragraph';
import { SearchHighlighter } from '../extensions/SearchHighlighter';
import { Strike } from '../extensions/Strike';
import { TextColor } from '../extensions/TextColor';
import { TextHighlight } from '../extensions/TextHighlight';
import { TextStyle } from '../extensions/TextStyle';
import { TypingBuffer } from '../extensions/TypingBuffer';
import { VirtualSelection } from '../extensions/VirtualSelection';
import { CoreCommands } from '../extensions/commands/CoreCommands';
import { Focus } from '../extensions/focus/Focus';
import { Link } from '../extensions/hyperlink/Link';
import { OldMention } from '../extensions/mention/OldMention';
import { TextMention } from '../extensions/mention/TextMention';
import { MentionsSuggestionsManager } from '../extensions/mention/SuggestionsManagerTypes';
import { HideContentDebugger } from '../extensions/HideContentDebugger';
import { CodeMark } from '../extensions/CodeMark';
import { ClipboardCopyHandler } from '../extensions/clipper/ClipboardCopyHandler';
import { HardBreak } from '../extensions/HardBreak';

// Types
import { Extensions } from '@tiptap/core';
import { Editor } from '@tiptap/react';
import { TiptapContent } from '../tiptapTypes';
import { Action } from 'redux';
import { SaveTiptapEditorContentFunction } from '../extensions/externalStoreSync/externalStoreSynchroniserTypes';
// import { PasteTransformer } from '../extensions/PasteTransformer';

export type UseCommonTiptapEditorExtensionsArgs = {
    saveContent?: SaveTiptapEditorContentFunction;
    onEmptyBackspace?: () => void;
    placeholder?: PlaceholderOptions['placeholder'];
    suggestionsManager?: MentionsSuggestionsManager;
    onHighlightsChanged?: (...args: unknown[]) => void;
    isEditable?: boolean;
    dispatch?: (...args: unknown[]) => unknown;
    clickableLinksWhenEditable?: boolean;
    toggleHyperlinkPopupAction: Action;
    getDebounceTime?: () => number;
    getDebounceCount?: () => number;
};

/**
 * The extensions common across all Milanote Tiptap element editors.
 */
const useCommonTiptapEditorExtensions = ({
    saveContent,
    onEmptyBackspace,
    placeholder,
    suggestionsManager,
    isEditable,
    dispatch,
    onHighlightsChanged,
    clickableLinksWhenEditable,
    toggleHyperlinkPopupAction,
    getDebounceTime = DEFAULT_EDITOR_SYNC_GET_DEBOUNCE_TIME,
    getDebounceCount = DEFAULT_EDITOR_SYNC_GET_DEBOUNCE_COUNT,
}: UseCommonTiptapEditorExtensionsArgs): Extensions =>
    useMemo(() => {
        const onEmptyDelete = (editor: Editor) => {
            // Save the empty content first, before deleting the element, so that the
            //  undo stack is correct
            const emptyContent = editor.getJSON() as TiptapContent;
            saveContent?.(emptyContent);

            // Ensure the editor is aligned with the content update before removal, to ensure
            //  a superfluous ELEMENT_UPDATE action isn't added to the undo stack
            editor.commands.syncFromExternalStore(emptyContent);

            // Similar to the onReturn handler, we need to defer the handling of
            //  this to prevent the Mousetrap delete element handler from being triggered
            defer(() => {
                onEmptyBackspace?.();
            });

            return !!onEmptyBackspace;
        };

        return [
            // The standard extensions for Tiptap
            //  Supports: Lists, quotes, code, inline styles, headings, history
            //  See: https://tiptap.dev/docs/editor/extensions/functionality/starterkit
            StarterKit.configure({
                document: false,
                strike: false,
                heading: false,
                paragraph: false,
                code: false,
                codeBlock: false,
                blockquote: false,
                bulletList: false,
                orderedList: false,
                bold: false,
                italic: false,
                horizontalRule: false,
                hardBreak: false,
            }),
            CoreCommands,
            CodeMark,
            Paragraph,
            Strike,
            Bold,
            Italic,
            Underline,
            // This extension is required for the TextColor extension to work
            TextStyle,
            TextColor,
            ClipboardCopyHandler,
            TextHighlight,
            HardBreak,
            // We need to create a new instance of this extension for each editor as it uses storage
            //  See: https://github.com/ueberdosis/tiptap/issues/4317#issuecomment-2201035342
            ExternalStoreSynchroniser.extend({}).configure({
                onDebouncedUpdate: saveContent,
                getDebounceTime,
                getDebounceCount,
            }),
            SearchHighlighter.configure({ onHighlightsChanged }),
            VirtualSelection,
            Focus,
            TypingBuffer,
            onEmptyBackspace && EmptyDelete.configure({ onEmptyDelete }),
            OldMention,
            suggestionsManager && TextMention.configure({ suggestionsManager }),
            // Show the placeholder text when the editor is empty
            Placeholder.configure({ placeholder, showOnlyWhenEditable: false }),
            // Allows hyperlinks
            //  See: https://tiptap.dev/docs/editor/extensions/marks/link#page-title
            Link.configure({ dispatch, isEditable, clickableLinksWhenEditable, toggleHyperlinkPopupAction }),
            // FIXME - WEB-13825 - The handlePaste from this extension prevents the "addPasteRules" method
            //   from running in extensions such as the InlineBoardLink extension.
            //   It's also running before the handlePaste of the TaskPaste extension, preventing multiple tasks
            //   from being created.  We'll need to find a way to solve these issues before enabling this extension.
            // PasteTransformer,
            process.env.NODE_ENV === 'production' && HideContentDebugger,
        ].filter(Boolean) as Extensions;
    }, [
        placeholder,
        suggestionsManager,
        isEditable,
        dispatch,
        saveContent,
        onEmptyBackspace,
        onHighlightsChanged,
        getDebounceTime,
        getDebounceCount,
    ]);

export default useCommonTiptapEditorExtensions;
